/**
 * Résolution du tri widget (config.sort) vers paramètres API SIT (order, asc, randomSeed).
 */

import { GeoJson } from "../types/apidae.d";
import {
  GeographicPerimeter,
  WidgetConfiguration,
  WidgetSortDirection,
  WidgetSortOption,
  WidgetTemplateType,
} from "../types/widgets.d";

export const DEFAULT_WIDGET_SORT_OPTION = WidgetSortOption.IDENTIFIANT;
export const DEFAULT_WIDGET_SORT_DIRECTION: WidgetSortDirection = "asc";

export interface ResolvedWidgetSort {
  apiOrder: string;
  asc: boolean;
  randomSeed?: string;
}

// Caches module-level : réinitialisés à chaque chargement de page, stables pour la pagination
const randomSeedCache = new Map<string, string>();
const lastSortOptionCache = new Map<string, string>();

function seedCacheKey(widgetId: string, modePreview: boolean): string {
  const id = widgetId || "0";
  return modePreview ? `preview:${id}` : `prod:${id}`;
}

function isAgendaTemplate(templateType: WidgetTemplateType): boolean {
  return (
    templateType === WidgetTemplateType.AGENDA ||
    templateType === WidgetTemplateType.AGENDA_MAP
  );
}

/** Zone circulaire complète (lat, long, rayon valides). */
export function isFullRadiusGeographicPerimeter(
  perimeter: GeographicPerimeter | undefined,
): boolean {
  if (!perimeter || perimeter.type !== "radius") {
    return false;
  }
  const { latitude, longitude, radius } = perimeter;
  return (
    latitude != null &&
    !Number.isNaN(latitude) &&
    longitude != null &&
    !Number.isNaN(longitude) &&
    radius != null &&
    !Number.isNaN(radius) &&
    radius > 0
  );
}

export function hasGeoSearchCenter(center?: GeoJson, radius?: number): boolean {
  return (
    center != null &&
    center.type === "Point" &&
    Array.isArray(center.coordinates) &&
    center.coordinates.length >= 2 &&
    radius != null &&
    radius > 0
  );
}

function getEffectiveSortConfig(config: WidgetConfiguration): {
  option: WidgetSortOption;
  order: WidgetSortDirection;
} {
  const option = config.sort?.option ?? DEFAULT_WIDGET_SORT_OPTION;
  const order = config.sort?.order ?? DEFAULT_WIDGET_SORT_DIRECTION;
  return { option, order };
}

function clearRandomSeed(widgetId: string, modePreview: boolean): void {
  randomSeedCache.delete(seedCacheKey(widgetId, modePreview));
}

function getOrCreateRandomSeed(
  widgetId: string,
  modePreview: boolean,
  forceNew: boolean,
): string {
  const key = seedCacheKey(widgetId, modePreview);
  if (forceNew) {
    randomSeedCache.delete(key);
  }
  let seed = randomSeedCache.get(key);
  if (!seed) {
    seed =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    randomSeedCache.set(key, seed);
  }
  return seed;
}

function trackSortOptionChange(
  widgetId: string,
  modePreview: boolean,
  option: WidgetSortOption,
): boolean {
  const key = seedCacheKey(widgetId, modePreview);
  const previous = lastSortOptionCache.get(key);
  const changed = previous !== option;
  lastSortOptionCache.set(key, option);
  return changed;
}

/**
 * Résout order / asc / randomSeed pour une requête API widget.
 */
export function resolveWidgetSort(
  config: WidgetConfiguration,
  widgetId: string,
  modePreview: boolean,
  center?: GeoJson,
  radius?: number,
): ResolvedWidgetSort {
  if (isAgendaTemplate(config.templateType)) {
    return {
      apiOrder: WidgetSortOption.DATE_OUVERTURE,
      asc: true,
    };
  }

  let { option, order } = getEffectiveSortConfig(config);
  const sortChanged = trackSortOptionChange(widgetId, modePreview, option);

  if (
    option === WidgetSortOption.DISTANCE &&
    !hasGeoSearchCenter(center, radius)
  ) {
    option = DEFAULT_WIDGET_SORT_OPTION;
    order = DEFAULT_WIDGET_SORT_DIRECTION;
  }

  const asc = order !== "desc";

  if (option === WidgetSortOption.RANDOM) {
    const forceNew = modePreview && sortChanged;
    if (forceNew) {
      clearRandomSeed(widgetId, modePreview);
    }
    return {
      apiOrder: WidgetSortOption.RANDOM,
      asc: true,
      randomSeed: getOrCreateRandomSeed(widgetId, modePreview, forceNew),
    };
  }

  return {
    apiOrder: option,
    asc,
  };
}
